import { Button } from "@progress/kendo-react-buttons";
import { Notification } from "@progress/kendo-react-notification";
import React, { PropsWithChildren } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import loggerClient from "@/libs/LoggerClient";
import { appendVersionToUrl, isChunkLoadError } from "./index";

const LazyComponentFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  const { t } = useTranslation();
  const chunkError = isChunkLoadError(error);

  React.useEffect(() => {
    if (chunkError) {
      loggerClient.recordEvent("UNABLE_TO_LOAD_COMPONENT", {
        error: (error as Error)?.message,
      });
    } else {
      loggerClient.recordError(error as Error);
    }
  }, [chunkError, error]);

  return (
    <div role="alert" style={{ padding: "1rem" }}>
      <Notification type={{ style: "error" }} style={{ marginBottom: "1rem" }}>
        <div>
          <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
            {chunkError
              ? t("lazyLoadError.titleForm")
              : t("lazyComponentError.genericTitle")}
          </p>
          <p style={{ margin: 0 }}>
            {chunkError
              ? t("lazyLoadError.messageLine1")
              : t("lazyComponentError.genericMessage")}
          </p>
          {chunkError && (
            <p style={{ margin: 0 }}>{t("lazyLoadError.messageLine2")}</p>
          )}
        </div>
      </Notification>
      <Button
        size="medium"
        type="button"
        themeColor="success"
        onClick={() =>
          chunkError
            ? (window.location.href = appendVersionToUrl(window.location.href))
            : resetErrorBoundary()
        }
        data-test-id="lazy-component-retry"
      >
        {chunkError ? t("lazyLoadError.retry") : t("lazyComponentError.retry")}
      </Button>
    </div>
  );
};

interface LazyComponentBoundaryProps {
  /** Fired when a child throws — lets the host chrome react (e.g. hide a Save footer). */
  onError?: () => void;
  /** Fired when the fallback's Retry resets the boundary. */
  onReset?: () => void;
}

export const LazyComponentBoundary: React.FC<
  PropsWithChildren<LazyComponentBoundaryProps>
> = ({ children, onError, onReset }) => (
  <ErrorBoundary
    FallbackComponent={LazyComponentFallback}
    onError={onError}
    onReset={onReset}
  >
    {children}
  </ErrorBoundary>
);
